import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { useAccessToken } from '@/auth/useAccessToken';
import { attachmentsApi } from '@/features/attachments/attachmentsApi';
import { useLightbox } from '@/features/attachments/Lightbox';

interface Props {
  html: string;
  className?: string;
}

/**
 * Renders saved message HTML.
 *
 * Body HTML originates from our own Lexical exporter (XSS-safe by construction);
 * inbound-email HTML is sanitized server-side (InboundHtmlCleaner). Inline images
 * are stored as <img data-attachment-id="..." src="/api/attachments/{id}"> — but
 * the download endpoint needs a bearer token, so a plain <img> can't load them.
 * After render we find those imgs, fetch each with auth → object URL, swap the
 * src, and wire a click to open the lightbox (full-size view).
 */
export function RichTextDisplay({ html, className }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const getAccessToken = useAccessToken();
  const lightbox = useLightbox();
  const tokenRef = useRef(getAccessToken);
  tokenRef.current = getAccessToken;
  const lightboxRef = useRef(lightbox);
  lightboxRef.current = lightbox;
  // Cache of attachmentId → blob object URL, persisted for this component's life.
  const urlCacheRef = useRef<Map<string, string>>(new Map());

  // Runs every render (cheap: cache hits short-circuit). React re-applies the
  // raw HTML — with the auth-protected /api/attachments src — whenever the
  // thread re-renders, so we must re-assert the blob src each time rather than
  // only once on [html] change. Blanking the src first avoids a 401/broken flash.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const cache = urlCacheRef.current;
    const imgs = Array.from(el.querySelectorAll('img[data-attachment-id]')) as HTMLImageElement[];

    // Skeleton classes shown while we fetch the blob. Without them an empty
     // <img> collapses to 0×0 and the layout jumps when the bytes arrive.
    const skeletonClasses = ['animate-pulse', 'bg-muted', 'min-h-24', 'min-w-32'];
    const showSkeleton = (img: HTMLImageElement) => img.classList.add(...skeletonClasses);
    const hideSkeleton = (img: HTMLImageElement) => img.classList.remove(...skeletonClasses);

    for (const img of imgs) {
      const id = img.getAttribute('data-attachment-id');
      if (!id) continue;

      if (!img.dataset.aesWired) {
        img.dataset.aesWired = '1';
        img.classList.add('max-h-80', 'max-w-full', 'rounded-md', 'border', 'cursor-zoom-in', 'my-1');
        img.addEventListener('click', () => lightboxRef.current.open(id, img.alt || 'attachment'));
      }

      const cached = cache.get(id);
      if (cached) {
        if (img.src !== cached) img.src = cached;
        hideSkeleton(img);
        continue;
      }
      if (img.currentSrc.startsWith('blob:')) {
        hideSkeleton(img);
        continue;
      }

      img.removeAttribute('src'); // don't let the browser hit the auth endpoint
      showSkeleton(img);
      attachmentsApi
        .fetchBlob(id, () => tokenRef.current())
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          cache.set(id, url);
          if (ref.current?.contains(img)) {
            img.src = url;
            hideSkeleton(img);
          }
        })
        .catch(() => undefined);
    }
  });

  // Revoke object URLs only when the component unmounts for good.
  useEffect(() => {
    const cache = urlCacheRef.current;
    return () => cache.forEach((u) => URL.revokeObjectURL(u));
  }, []);

  return (
    <div
      ref={ref}
      className={cn(
        'text-sm leading-relaxed',
        '[&_p]:mb-2 [&_p:last-child]:mb-0',
        '[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2',
        '[&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2',
        '[&_strong]:font-semibold [&_em]:italic',
        className
      )}
      // HTML is from our Lexical exporter or the server-side sanitizer.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
